<template>
    <div class="batch-import">
        <!-- Trigger Button -->
        <button
            type="button"
            class="btn-import"
            @click="showModal = true"
        >
            <svg xmlns="http://www.w3.org/2000/svg" class="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import from CSV
        </button>

        <!-- Modal Overlay -->
        <transition name="fade">
            <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
                <div class="modal-container">
                    <!-- Header -->
                    <div class="modal-header">
                        <h3>
                            <template v-if="step === 'upload'">Import Supporting Campaigns</template>
                            <template v-else-if="step === 'mapping'">Map Columns</template>
                            <template v-else-if="step === 'preview'">Preview Import</template>
                            <template v-else-if="step === 'result'">Import Complete</template>
                        </h3>
                        <button class="btn-close" @click="closeModal" :disabled="importing">
                            <svg xmlns="http://www.w3.org/2000/svg" class="close-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <!-- Step 1: Upload -->
                    <div v-if="step === 'upload'" class="modal-body">
                        <p class="helper-text">
                            Upload a CSV file to create multiple supporting campaigns under
                            <strong>{{ parentName }}</strong>.
                        </p>

                        <!-- Download Template -->
                        <div class="template-section">
                            <a
                                :href="`/campaign/${parentId}/import/template`"
                                class="btn-template"
                                download
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="icon-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download CSV Template
                            </a>
                            <span class="template-hint">Fill in this template, then upload it below.</span>
                        </div>

                        <!-- File Upload -->
                        <div class="upload-section">
                            <label class="upload-area" :class="{ 'drag-over': dragging }"
                                @dragover.prevent="dragging = true"
                                @dragleave.prevent="dragging = false"
                                @drop.prevent="handleDrop"
                            >
                                <input
                                    type="file"
                                    ref="fileInput"
                                    accept=".csv,.txt"
                                    @change="handleFileSelect"
                                    class="hidden-input"
                                />
                                <div class="upload-content" v-if="!selectedFile">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="upload-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <span class="upload-text">
                                        <strong>Click to upload</strong> or drag and drop
                                    </span>
                                    <span class="upload-hint">CSV files only (max 200 rows)</span>
                                </div>
                                <div class="upload-content file-selected" v-else>
                                    <svg xmlns="http://www.w3.org/2000/svg" class="file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <span class="file-name">{{ selectedFile.name }}</span>
                                    <button type="button" class="btn-remove-file" @click.stop="clearFile">✕ Remove</button>
                                </div>
                            </label>
                        </div>

                        <!-- Error from parse -->
                        <div v-if="parseError" class="alert alert-error">{{ parseError }}</div>

                        <!-- Actions -->
                        <div class="modal-actions">
                            <button class="btn-cancel" @click="closeModal">Cancel</button>
                            <button
                                class="btn-preview"
                                @click="uploadAndPreview"
                                :disabled="!selectedFile || previewing"
                            >
                                <span v-if="previewing" class="spinner"></span>
                                {{ previewing ? 'Validating...' : 'Preview Import' }}
                            </button>
                        </div>
                    </div>

                    <!-- Step 1.5: Column Mapping -->
                    <div v-if="step === 'mapping'" class="modal-body">
                        <p class="helper-text">
                            We detected <strong>{{ mappingData.row_count }}</strong> data rows.
                            Please confirm or adjust the column mapping below.
                        </p>

                        <!-- Mapping status -->
                        <div class="mapping-status" :class="mappingValid ? 'mapping-ok' : 'mapping-incomplete'">
                            <template v-if="mappingValid">
                                ✅ Required fields mapped — ready to preview.
                            </template>
                            <template v-else>
                                ⚠️ Please map at least <strong>Campaign Name</strong> and <strong>Organization Name</strong>.
                            </template>
                        </div>

                        <!-- Mapping table -->
                        <div class="mapping-table-wrapper">
                            <table class="mapping-table">
                                <thead>
                                    <tr>
                                        <th>CSV Column</th>
                                        <th>Sample Data</th>
                                        <th>Maps To</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(header, idx) in mappingData.raw_headers" :key="idx">
                                        <td class="col-header">
                                            <strong>{{ header }}</strong>
                                        </td>
                                        <td class="col-sample">
                                            <span class="sample-values">
                                                <template v-for="(sampleRow, si) in mappingData.sample_rows" :key="si">
                                                    <span class="sample-value">{{ sampleRow[idx] || '(empty)' }}</span>
                                                    <template v-if="si < mappingData.sample_rows.length - 1">, </template>
                                                </template>
                                            </span>
                                        </td>
                                        <td class="col-mapping">
                                            <select
                                                v-model="userColumnMap[idx]"
                                                class="mapping-select"
                                                :class="{
                                                    'mapped-auto': userColumnMap[idx] && autoMappedFields.includes(idx),
                                                    'mapped-user': userColumnMap[idx] && !autoMappedFields.includes(idx),
                                                    'mapped-skip': !userColumnMap[idx]
                                                }"
                                            >
                                                <option value="">— Skip this column —</option>
                                                <option
                                                    v-for="(label, fieldKey) in mappingData.available_fields"
                                                    :key="fieldKey"
                                                    :value="fieldKey"
                                                    :disabled="isFieldTaken(fieldKey, idx)"
                                                >
                                                    {{ label }}
                                                    <template v-if="isFieldTaken(fieldKey, idx)"> (already mapped)</template>
                                                </option>
                                            </select>
                                            <span v-if="userColumnMap[idx] && autoMappedFields.includes(idx)" class="map-badge auto-badge">auto</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Error from mapping validation -->
                        <div v-if="parseError" class="alert alert-error">{{ parseError }}</div>

                        <!-- Actions -->
                        <div class="modal-actions">
                            <button class="btn-cancel" @click="step = 'upload'">← Back</button>
                            <button
                                class="btn-preview"
                                @click="proceedToPreview"
                                :disabled="!mappingValid || previewing"
                            >
                                <span v-if="previewing" class="spinner"></span>
                                {{ previewing ? 'Validating...' : 'Preview Import' }}
                            </button>
                        </div>
                    </div>

                    <!-- Step 2: Preview -->
                    <div v-if="step === 'preview'" class="modal-body">
                        <!-- Summary -->
                        <div class="preview-summary">
                            <div class="summary-stat summary-valid">
                                <span class="stat-number">{{ previewData.valid_count }}</span>
                                <span class="stat-label">Valid</span>
                            </div>
                            <div class="summary-stat summary-errors" v-if="previewData.error_count > 0">
                                <span class="stat-number">{{ previewData.error_count }}</span>
                                <span class="stat-label">Errors</span>
                            </div>
                            <div class="summary-stat summary-total">
                                <span class="stat-number">{{ previewData.total_rows }}</span>
                                <span class="stat-label">Total Rows</span>
                            </div>
                        </div>

                        <!-- Admin: new user warning -->
                        <div v-if="previewData.new_users > 0" class="admin-user-warning">
                            <svg xmlns="http://www.w3.org/2000/svg" class="warning-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span>
                                <strong>{{ previewData.new_users }} new {{ previewData.new_users !== 1 ? 'people don\'t' : 'person doesn\'t' }} have an account yet.</strong>
                                We'll set one up for them automatically — they can sign in later using their email.
                            </span>
                        </div>

                        <!-- Preview Table -->
                        <div class="preview-table-wrapper">
                            <table class="preview-table">
                                <thead>
                                    <tr>
                                        <th class="col-status">#</th>
                                        <th>Campaign Name</th>
                                        <th>Organization</th>
                                        <th>Goal</th>
                                        <th>City</th>
                                        <th v-if="previewData.has_email_column">Owner</th>
                                        <th class="col-status">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <!-- Valid rows -->
                                    <tr v-for="row in previewData.valid_rows" :key="'v-' + row._row" class="row-valid">
                                        <td>{{ row._row }}</td>
                                        <td>{{ row.campaign_name }}</td>
                                        <td>{{ row.organization_name }}</td>
                                        <td>{{ row.goal || 'Default' }}</td>
                                        <td>{{ row.city || 'Inherited' }}</td>
                                        <td v-if="previewData.has_email_column">
                                            <span v-if="row._user_status === 'existing'" class="user-badge user-existing" title="Existing user account">
                                                👤 {{ row.email }}
                                            </span>
                                            <span v-else-if="row._user_status === 'new'" class="user-badge user-new" title="New — we'll create an account for them">
                                                🆕 {{ row.email }}
                                            </span>
                                            <span v-else class="user-badge user-admin">
                                                You (admin)
                                            </span>
                                        </td>
                                        <td><span class="badge badge-valid">✓</span></td>
                                    </tr>
                                    <!-- Error rows -->
                                    <template v-for="err in previewData.errors" :key="'e-' + err.row">
                                        <tr class="row-error">
                                            <td>{{ err.row }}</td>
                                            <td>{{ err.data?.campaign_name || '(empty)' }}</td>
                                            <td>{{ err.data?.organization_name || '(empty)' }}</td>
                                            <td>{{ err.data?.goal || '—' }}</td>
                                            <td>{{ err.data?.city || '—' }}</td>
                                            <td v-if="previewData.has_email_column">{{ err.data?.email || '—' }}</td>
                                            <td><span class="badge badge-error">✕</span></td>
                                        </tr>
                                        <tr class="row-error-detail">
                                            <td></td>
                                            <td :colspan="previewData.has_email_column ? 6 : 5">
                                                <ul class="error-list">
                                                    <li v-for="(fieldErr, fi) in err.fields" :key="fi">
                                                        <strong>{{ fieldErr.field }}:</strong> {{ fieldErr.message }}
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>

                        <!-- Actions -->
                        <div class="modal-actions">
                            <button class="btn-cancel" @click="step = 'mapping'">← Back to Mapping</button>
                            <button
                                class="btn-import-execute"
                                @click="executeImport"
                                :disabled="previewData.valid_count === 0 || importing"
                            >
                                <span v-if="importing" class="spinner"></span>
                                {{ importing
                                    ? 'Importing...'
                                    : `Import ${previewData.valid_count} Campaign${previewData.valid_count !== 1 ? 's' : ''}`
                                }}
                            </button>
                        </div>
                    </div>

                    <!-- Step 3: Result -->
                    <div v-if="step === 'result'" class="modal-body">
                        <div class="result-icon" :class="resultData.success ? 'result-success' : 'result-failed'">
                            <svg v-if="resultData.success" xmlns="http://www.w3.org/2000/svg" class="result-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="result-svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>

                        <p class="result-message">{{ resultData.message }}</p>

                        <div v-if="resultData.queued" class="result-queued-note">
                            Large imports are processed in the background. Refresh the page in a moment to see the new campaigns.
                        </div>

                        <div v-if="resultData.import_errors && resultData.import_errors.length > 0" class="result-errors">
                            <h4>Import Errors:</h4>
                            <ul>
                                <li v-for="(err, idx) in resultData.import_errors" :key="idx">
                                    Row {{ err.row }}: {{ err.message }}
                                </li>
                            </ul>
                        </div>

                        <div class="modal-actions">
                            <button class="btn-done" @click="closeAndRefresh">
                                {{ resultData.success ? 'Done — Refresh Page' : 'Close' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'BatchImportModal',

    props: {
        parentId: {
            type: [Number, String],
            required: true,
        },
        parentName: {
            type: String,
            default: 'this campaign',
        },
    },

    data() {
        return {
            showModal: false,
            step: 'upload',     // upload | mapping | preview | result
            dragging: false,
            selectedFile: null,
            parseError: '',
            previewing: false,
            importing: false,
            // Column mapping
            mappingData: {
                raw_headers: [],
                auto_map: {},
                unmapped: [],
                needs_mapping: false,
                row_count: 0,
                sample_rows: [],
                available_fields: {},
            },
            userColumnMap: {},      // idx → field_name or ''
            autoMappedFields: [],   // indices that were auto-mapped
            // Preview
            previewData: {
                total_rows: 0,
                valid_count: 0,
                error_count: 0,
                valid_rows: [],
                errors: [],
            },
            resultData: {
                success: false,
                message: '',
                queued: false,
                import_errors: [],
            },
        };
    },

    computed: {
        /**
         * Are the two required fields (campaign_name, organization_name) mapped?
         */
        mappingValid() {
            const mapped = Object.values(this.userColumnMap);
            return mapped.includes('campaign_name') && mapped.includes('organization_name');
        },
    },

    methods: {
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (file) {
                this.selectedFile = file;
                this.parseError = '';
            }
        },

        handleDrop(event) {
            this.dragging = false;
            const file = event.dataTransfer.files[0];
            if (file) {
                this.selectedFile = file;
                this.parseError = '';
            }
        },

        clearFile() {
            this.selectedFile = null;
            this.parseError = '';
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        /**
         * Step 1: Upload file → detect headers → decide mapping or auto-preview.
         */
        async uploadAndPreview() {
            if (!this.selectedFile) return;

            this.previewing = true;
            this.parseError = '';

            const formData = new FormData();
            formData.append('file', this.selectedFile);

            try {
                // First, detect headers
                const response = await axios.post(
                    `/campaign/${this.parentId}/import/detect-headers`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                if (response.data.success) {
                    this.mappingData = response.data;

                    // Initialize userColumnMap from auto_map
                    this.userColumnMap = {};
                    this.autoMappedFields = [];
                    for (const [idx, field] of Object.entries(response.data.auto_map)) {
                        this.userColumnMap[idx] = field || '';
                        if (field) {
                            this.autoMappedFields.push(parseInt(idx));
                        }
                    }

                    if (response.data.needs_mapping) {
                        // Show mapping UI
                        this.step = 'mapping';
                    } else {
                        // All columns auto-mapped, skip to preview
                        await this.proceedToPreview();
                    }
                } else {
                    this.parseError = response.data.message || 'Failed to parse file.';
                }
            } catch (error) {
                if (error.response?.data?.message) {
                    this.parseError = error.response.data.message;
                } else if (error.response?.status === 422 && error.response?.data?.errors?.file) {
                    this.parseError = error.response.data.errors.file[0];
                } else {
                    this.parseError = 'Failed to upload file. Please try again.';
                }
            } finally {
                this.previewing = false;
            }
        },

        /**
         * Check if a field is already mapped to another column.
         */
        isFieldTaken(fieldKey, currentIdx) {
            for (const [idx, val] of Object.entries(this.userColumnMap)) {
                if (parseInt(idx) !== currentIdx && val === fieldKey) {
                    return true;
                }
            }
            return false;
        },

        /**
         * Send column mapping + file to preview endpoint.
         */
        async proceedToPreview() {
            this.previewing = true;
            this.parseError = '';

            const formData = new FormData();
            formData.append('file', this.selectedFile);
            formData.append('column_map', JSON.stringify(this.userColumnMap));

            try {
                const response = await axios.post(
                    `/campaign/${this.parentId}/import/preview`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                if (response.data.success) {
                    this.previewData = response.data;
                    this.step = 'preview';
                } else {
                    this.parseError = response.data.message || 'Failed to parse file.';
                    // If we were on mapping step, stay there
                    if (this.step !== 'mapping') {
                        this.step = 'upload';
                    }
                }
            } catch (error) {
                const msg = error.response?.data?.message || 'Failed to validate file. Please try again.';
                if (this.step === 'mapping') {
                    this.parseError = msg;
                } else {
                    this.parseError = msg;
                    this.step = 'upload';
                }
            } finally {
                this.previewing = false;
            }
        },

        async executeImport() {
            this.importing = true;

            const formData = new FormData();
            formData.append('file', this.selectedFile);
            formData.append('column_map', JSON.stringify(this.userColumnMap));

            try {
                const response = await axios.post(
                    `/campaign/${this.parentId}/import/execute`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                this.resultData = response.data;
                this.step = 'result';

                if (response.data.success) {
                    this.$emit('import-complete', response.data);
                }
            } catch (error) {
                this.resultData = {
                    success: false,
                    message: error.response?.data?.message || 'Import failed. Please try again.',
                    queued: false,
                    import_errors: error.response?.data?.import_errors || [],
                };
                this.step = 'result';
            } finally {
                this.importing = false;
            }
        },

        closeModal() {
            if (this.importing) return;
            this.resetState();
            this.showModal = false;
        },

        closeAndRefresh() {
            this.showModal = false;
            if (this.resultData.success) {
                window.location.reload();
            }
            this.resetState();
        },

        resetState() {
            this.step = 'upload';
            this.selectedFile = null;
            this.parseError = '';
            this.previewing = false;
            this.importing = false;
            this.mappingData = { raw_headers: [], auto_map: {}, unmapped: [], needs_mapping: false, row_count: 0, sample_rows: [], available_fields: {} };
            this.userColumnMap = {};
            this.autoMappedFields = [];
            this.previewData = { total_rows: 0, valid_count: 0, error_count: 0, valid_rows: [], errors: [] };
            this.resultData = { success: false, message: '', queued: false, import_errors: [] };
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },
    },
};
</script>

<style scoped>
.batch-import {
    display: inline-block;
}

.btn-import {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    background: linear-gradient(135deg, #3B82F6, #2563EB);
    color: white;
    border: none;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-import:hover {
    background: linear-gradient(135deg, #2563EB, #1D4ED8);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-import .icon {
    width: 1.25rem;
    height: 1.25rem;
}

/* Modal */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 0.75rem;
    width: 100%;
    max-width: 720px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #E5E7EB;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
}

.btn-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    color: #6B7280;
    border-radius: 0.25rem;
}

.btn-close:hover {
    color: #111827;
    background: #F3F4F6;
}

.close-icon {
    width: 1.25rem;
    height: 1.25rem;
}

.modal-body {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
}

.helper-text {
    color: #4B5563;
    margin: 0 0 1.25rem 0;
    font-size: 0.9rem;
}

/* Template download */
.template-section {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
}

.btn-template {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    border: 1.5px solid #D1D5DB;
    border-radius: 0.375rem;
    color: #374151;
    text-decoration: none;
    font-size: 0.8125rem;
    font-weight: 500;
    transition: all 0.15s;
}

.btn-template:hover {
    border-color: #9CA3AF;
    background: #F9FAFB;
}

.icon-sm {
    width: 1rem;
    height: 1rem;
}

.template-hint {
    color: #9CA3AF;
    font-size: 0.8125rem;
}

/* Upload area */
.upload-area {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed #D1D5DB;
    border-radius: 0.75rem;
    padding: 2rem;
    cursor: pointer;
    transition: all 0.2s;
    background: #FAFAFA;
}

.upload-area:hover,
.upload-area.drag-over {
    border-color: #3B82F6;
    background: #EFF6FF;
}

.hidden-input {
    display: none;
}

.upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    text-align: center;
}

.upload-icon {
    width: 2.5rem;
    height: 2.5rem;
    color: #9CA3AF;
}

.upload-text {
    color: #4B5563;
    font-size: 0.875rem;
}

.upload-hint {
    color: #9CA3AF;
    font-size: 0.75rem;
}

.file-selected {
    flex-direction: row;
    gap: 0.75rem;
}

.file-icon {
    width: 1.5rem;
    height: 1.5rem;
    color: #3B82F6;
}

.file-name {
    font-weight: 500;
    color: #111827;
    font-size: 0.875rem;
}

.btn-remove-file {
    background: none;
    border: none;
    color: #EF4444;
    font-size: 0.8125rem;
    cursor: pointer;
    font-weight: 500;
}

/* Alerts */
.alert {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-top: 1rem;
    font-size: 0.875rem;
}

.alert-error {
    background: #FEE2E2;
    border: 1px solid #FCA5A5;
    color: #991B1B;
}

/* Actions */
.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #F3F4F6;
}

.btn-cancel {
    padding: 0.5rem 1.25rem;
    background: white;
    border: 1.5px solid #D1D5DB;
    border-radius: 0.375rem;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
}

.btn-cancel:hover {
    background: #F9FAFB;
}

.btn-preview,
.btn-import-execute {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1.25rem;
    background: #97d700;
    color: #1a1a1a;
    border: none;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-preview:hover:not(:disabled),
.btn-import-execute:hover:not(:disabled) {
    background: #85c000;
}

.btn-preview:disabled,
.btn-import-execute:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-done {
    padding: 0.625rem 1.5rem;
    background: linear-gradient(135deg, #EC4899, #DB2777);
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
}

.btn-done:hover {
    background: linear-gradient(135deg, #DB2777, #BE185D);
}

/* Preview */
.preview-summary {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.25rem;
}

.summary-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.75rem 1.25rem;
    border-radius: 0.5rem;
    min-width: 5rem;
}

.summary-valid {
    background: #D1FAE5;
    color: #065F46;
}

.summary-errors {
    background: #FEE2E2;
    color: #991B1B;
}

.summary-total {
    background: #F3F4F6;
    color: #374151;
}

.stat-number {
    font-size: 1.5rem;
    font-weight: 700;
}

.stat-label {
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}

.preview-table-wrapper {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #E5E7EB;
    border-radius: 0.5rem;
}

.preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8125rem;
}

.preview-table th {
    background: #F9FAFB;
    padding: 0.625rem 0.75rem;
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #E5E7EB;
    position: sticky;
    top: 0;
    z-index: 1;
}

.preview-table td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #F3F4F6;
}

.col-status {
    width: 3rem;
    text-align: center;
}

.row-valid td {
    background: #F0FDF4;
}

.row-error td {
    background: #FEF2F2;
}

.row-error-detail td {
    background: #FEF2F2;
    padding-top: 0;
    padding-bottom: 0.5rem;
}

.badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    font-size: 0.75rem;
    font-weight: 700;
}

.badge-valid {
    background: #D1FAE5;
    color: #059669;
}

.badge-error {
    background: #FEE2E2;
    color: #DC2626;
}

.error-list {
    margin: 0;
    padding: 0 0 0 1rem;
    font-size: 0.75rem;
    color: #991B1B;
}

.error-list li {
    margin-bottom: 0.125rem;
}

/* Admin user warning */
.admin-user-warning {
    display: flex;
    align-items: flex-start;
    gap: 0.625rem;
    padding: 0.75rem 1rem;
    background: #FFFBEB;
    border: 1px solid #FCD34D;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    font-size: 0.875rem;
    color: #92400E;
}

.warning-icon {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    color: #D97706;
    margin-top: 0.125rem;
}

/* User badges */
.user-badge {
    font-size: 0.75rem;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
    display: inline-block;
}

.user-existing {
    background: #DBEAFE;
    color: #1E40AF;
}

.user-new {
    background: #FEF3C7;
    color: #92400E;
}

.user-admin {
    background: #F3F4F6;
    color: #6B7280;
    font-style: italic;
}

/* Result */
.result-icon {
    text-align: center;
    margin-bottom: 1rem;
}

.result-svg {
    width: 3.5rem;
    height: 3.5rem;
}

.result-success .result-svg {
    color: #059669;
}

.result-failed .result-svg {
    color: #DC2626;
}

.result-message {
    text-align: center;
    font-size: 1rem;
    font-weight: 500;
    color: #111827;
    margin-bottom: 1rem;
}

.result-queued-note {
    text-align: center;
    padding: 0.75rem;
    background: #EFF6FF;
    border: 1px solid #BFDBFE;
    border-radius: 0.5rem;
    color: #1E40AF;
    font-size: 0.875rem;
    margin-bottom: 1rem;
}

.result-errors {
    margin-top: 1rem;
}

.result-errors h4 {
    color: #991B1B;
    font-size: 0.875rem;
    margin: 0 0 0.5rem 0;
}

.result-errors ul {
    font-size: 0.8125rem;
    color: #7F1D1D;
    padding-left: 1.25rem;
}

/* Spinner */
.spinner {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Column Mapping */
.mapping-status {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1.25rem;
    font-size: 0.875rem;
    font-weight: 500;
}

.mapping-ok {
    background: #D1FAE5;
    border: 1px solid #6EE7B7;
    color: #065F46;
}

.mapping-incomplete {
    background: #FEF3C7;
    border: 1px solid #FCD34D;
    color: #92400E;
}

.mapping-table-wrapper {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #E5E7EB;
    border-radius: 0.5rem;
}

.mapping-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8125rem;
}

.mapping-table th {
    background: #F9FAFB;
    padding: 0.625rem 0.75rem;
    text-align: left;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #E5E7EB;
    position: sticky;
    top: 0;
    z-index: 1;
}

.mapping-table td {
    padding: 0.625rem 0.75rem;
    border-bottom: 1px solid #F3F4F6;
    vertical-align: middle;
}

.col-header {
    white-space: nowrap;
    color: #111827;
}

.col-sample {
    color: #6B7280;
    font-size: 0.75rem;
    max-width: 200px;
}

.sample-values {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.sample-value {
    background: #F3F4F6;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

.col-mapping {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.mapping-select {
    padding: 0.375rem 0.5rem;
    border: 1.5px solid #D1D5DB;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    min-width: 180px;
    background: white;
    cursor: pointer;
}

.mapping-select:focus {
    border-color: #3B82F6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.mapping-select.mapped-auto {
    border-color: #6EE7B7;
    background: #F0FDF4;
}

.mapping-select.mapped-user {
    border-color: #93C5FD;
    background: #EFF6FF;
}

.mapping-select.mapped-skip {
    border-color: #D1D5DB;
    color: #9CA3AF;
}

.map-badge {
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    letter-spacing: 0.05em;
}

.auto-badge {
    background: #D1FAE5;
    color: #059669;
}

@media (max-width: 640px) {
    .modal-container {
        max-height: 95vh;
    }

    .preview-summary {
        flex-wrap: wrap;
    }

    .template-section {
        flex-direction: column;
        align-items: flex-start;
    }

    .mapping-select {
        min-width: 140px;
    }

    .col-sample {
        display: none;
    }
}
</style>
